import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload,
  Trash2,
  Pencil,
  Check,
  X,
  FileText,
  Search,
  Download,
} from 'lucide-react';
import {
  getCollateralItems,
  uploadCollateralItem,
  updateCollateralItem,
  deleteCollateralItem,
} from '../api/client.js';
import type { CollateralItem } from '../api/client.js';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function CollateralPage() {
  const [items, setItems] = useState<CollateralItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // Delete confirmation
  const [deletingItem, setDeletingItem] = useState<CollateralItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const data = await getCollateralItems();
      setItems(data.items);
    } catch {
      setError('Failed to load collateral items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      for (const file of Array.from(files)) {
        await uploadCollateralItem(file);
      }
      setSuccess(`Uploaded ${files.length} file${files.length !== 1 ? 's' : ''}`);
      await loadItems();
    } catch {
      setError('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startEdit = (item: CollateralItem) => {
    setEditingId(item.id);
    setEditLabel(item.label || '');
    setEditDescription(item.description || '');
    setEditCategory(item.category || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await updateCollateralItem(editingId, {
        label: editLabel || undefined,
        description: editDescription || undefined,
        category: editCategory || undefined,
      });
      setEditingId(null);
      await loadItems();
    } catch {
      setError('Failed to update item');
    }
  };

  const handleToggleDefault = async (item: CollateralItem) => {
    try {
      await updateCollateralItem(item.id, { isDefault: !item.isDefault });
      await loadItems();
    } catch {
      setError('Failed to update default status');
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    setDeleteLoading(true);
    try {
      await deleteCollateralItem(deletingItem.id);
      setDeletingItem(null);
      setSuccess('Item deleted');
      await loadItems();
    } catch {
      setError('Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter & group by category
  const filtered = items.filter((item) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.label || '').toLowerCase().includes(term) ||
      item.originalFilename.toLowerCase().includes(term) ||
      (item.category || '').toLowerCase().includes(term) ||
      (item.description || '').toLowerCase().includes(term)
    );
  });

  // Group by category
  const grouped = new Map<string, CollateralItem[]>();
  for (const item of filtered) {
    const cat = item.category || 'Uncategorized';
    const list = grouped.get(cat) || [];
    list.push(item);
    grouped.set(cat, list);
  }
  const sortedCategories = [...grouped.keys()].sort((a, b) => {
    if (a === 'Uncategorized') return 1;
    if (b === 'Uncategorized') return -1;
    return a.localeCompare(b);
  });

  // Unique categories for autocomplete
  const existingCategories = [...new Set(items.map((i) => i.category).filter(Boolean))] as string[];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading collateral library...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Collateral Library</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage reusable documents like spec sheets, brochures, and comparison charts that can be
            attached to any quote.
          </p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.svg,.csv,.txt,.zip"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-md border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-300">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline hover:text-red-200">
            Dismiss
          </button>
        </div>
      )}
      {success && (
        <div className="rounded-md border border-green-800 bg-green-900/30 px-4 py-3 text-sm text-green-300">
          {success}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search collateral..."
          className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pl-10 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {/* Items grouped by category */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-700" />
          <h3 className="mt-4 text-lg font-medium text-gray-400">
            {items.length === 0 ? 'No collateral yet' : 'No matching items'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {items.length === 0
              ? 'Upload spec sheets, brochures, or other documents to include with your quotes.'
              : 'Try adjusting your search term.'}
          </p>
        </div>
      ) : (
        sortedCategories.map((category) => {
          const categoryItems = grouped.get(category) || [];
          return (
            <div key={category}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {category} ({categoryItems.length})
              </h2>
              <div className="overflow-hidden rounded-lg border border-gray-800">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-gray-900/50">
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                        Document
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                        Description
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                        Size
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium uppercase text-gray-500">
                        Default
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {categoryItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-800/50">
                        {editingId === item.id ? (
                          <>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editLabel}
                                onChange={(e) => setEditLabel(e.target.value)}
                                placeholder="Label"
                                className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                              />
                              <div className="mt-1 text-xs text-gray-500">
                                {item.originalFilename}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Description"
                                className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                              />
                              <div className="relative mt-1">
                                <input
                                  type="text"
                                  value={editCategory}
                                  onChange={(e) => setEditCategory(e.target.value)}
                                  placeholder="Category"
                                  list="category-options"
                                  className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-gray-100 focus:border-brand-500 focus:outline-none"
                                />
                                <datalist id="category-options">
                                  {existingCategories.map((cat) => (
                                    <option key={cat} value={cat} />
                                  ))}
                                </datalist>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-gray-500">
                              {formatFileSize(item.fileSize)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => handleToggleDefault(item)}
                                className={`inline-flex h-5 w-5 items-center justify-center rounded ${
                                  item.isDefault
                                    ? 'bg-brand-600 text-white'
                                    : 'border border-gray-600 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {item.isDefault && <Check className="h-3 w-3" />}
                              </button>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={saveEdit}
                                  className="rounded p-1 text-green-400 hover:bg-gray-700"
                                  title="Save"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="rounded p-1 text-gray-400 hover:bg-gray-700"
                                  title="Cancel"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 flex-shrink-0 text-gray-500" />
                                <div>
                                  <div className="text-sm font-medium text-gray-200">
                                    {item.label || item.originalFilename}
                                  </div>
                                  {item.label && (
                                    <div className="text-xs text-gray-500">
                                      {item.originalFilename}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400">
                              {item.description || '—'}
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-gray-500">
                              {formatFileSize(item.fileSize)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => handleToggleDefault(item)}
                                className={`inline-flex h-5 w-5 items-center justify-center rounded ${
                                  item.isDefault
                                    ? 'bg-brand-600 text-white'
                                    : 'border border-gray-600 text-gray-600 hover:border-gray-400'
                                }`}
                                title={
                                  item.isDefault
                                    ? 'Remove from defaults'
                                    : 'Set as default (auto-selected for new emails)'
                                }
                              >
                                {item.isDefault && <Check className="h-3 w-3" />}
                              </button>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <a
                                  href={`/api/collateral/download/${item.id}`}
                                  className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                                <button
                                  onClick={() => startEdit(item)}
                                  className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setDeletingItem(item)}
                                  className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-red-400"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* Delete Confirmation Dialog */}
      {deletingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-100">Delete Document</h3>
            <p className="mt-2 text-sm text-gray-400">
              Are you sure you want to delete{' '}
              <strong className="text-gray-200">
                {deletingItem.label || deletingItem.originalFilename}
              </strong>
              ? This will also remove it from any quotes it's attached to.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setDeletingItem(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
